/**
 * Settings page for configuration management
 */

import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useConfig } from "@/lib/config/config-context";
import { ModeConfigDialog } from "./mode-config-dialog";
import { LocalConfigDialog } from "./local-config-dialog";
import { RemoteConfigDialog } from "./remote-config-dialog";
import {
  Server,
  Monitor,
  Globe,
  Settings2,
  Key,
  Laptop,
  Cloud,
} from "lucide-react";

export function SettingsPage() {
  const { config, isLocalMode } = useConfig();
  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const [localDialogOpen, setLocalDialogOpen] = useState(false);
  const [remoteDialogOpen, setRemoteDialogOpen] = useState(false);

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-hidden">
      <div className="shrink-0">
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Configure your Harmony Management environment and connection settings
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-y-auto">
        {/* Current Configuration Overview */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Current Configuration
          </CardTitle>
          <CardDescription>
            Overview of your current environment and connection settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isLocalMode ? (
                <Laptop className="h-4 w-4" />
              ) : (
                <Cloud className="h-4 w-4" />
              )}
              <span className="font-medium">Operation Mode</span>
            </div>
            <Badge variant={isLocalMode ? "secondary" : "default"}>
              {config.mode.toUpperCase()}
            </Badge>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span className="font-medium">Base URL</span>
            </div>
            <span className="text-muted-foreground font-mono text-sm">
              {isLocalMode ? config.localBaseUrl : config.tenantsBaseUrl}
            </span>
          </div>

          {isLocalMode && config.localTenantId && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                <span className="font-medium">Local Tenant ID</span>
              </div>
              <span className="text-muted-foreground font-mono text-sm">
                {config.localTenantId}
              </span>
            </div>
          )}

          {!isLocalMode && config.descopeProjectId && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                <span className="font-medium">Descope Project ID</span>
              </div>
              <span className="text-muted-foreground font-mono text-sm">
                {config.descopeProjectId}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configuration Options */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Operation Mode */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Monitor className="h-5 w-5" />
              Operation Mode
            </CardTitle>
            <CardDescription>
              Switch between local development and remote production modes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="text-sm">
                Current mode:{" "}
                <Badge variant={isLocalMode ? "secondary" : "default"}>
                  {config.mode.toUpperCase()}
                </Badge>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setModeDialogOpen(true);
                }}
                className="w-full"
              >
                Change Mode
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Local Development Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Laptop className="h-5 w-5" />
              Local Development
            </CardTitle>
            <CardDescription>
              Configure settings for local development mode
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="space-y-1 text-sm">
                <div>
                  Server:{" "}
                  <span className="text-muted-foreground font-mono">
                    {config.localBaseUrl}
                  </span>
                </div>
                <div>
                  Tenant ID:{" "}
                  <span className="text-muted-foreground font-mono">
                    {config.localTenantId ?? "Not set"}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setLocalDialogOpen(true);
                }}
                className="w-full"
              >
                Configure Local
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Remote Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              Remote Settings
            </CardTitle>
            <CardDescription>
              Configure remote server URLs and authentication
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="space-y-1 text-sm">
                <div>
                  Server:{" "}
                  <span className="text-muted-foreground font-mono">
                    {config.tenantsBaseUrl}
                  </span>
                </div>
                <div>
                  Descope Project:{" "}
                  <span className="text-muted-foreground font-mono">
                    {config.descopeProjectId ?? "Not set"}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setRemoteDialogOpen(true);
                }}
                className="w-full"
              >
                Configure Remote
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Connection Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              Connection Status
            </CardTitle>
            <CardDescription>
              Current connection and authentication status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="space-y-1 text-sm">
                <div>
                  Mode:{" "}
                  <span className="text-muted-foreground font-mono">
                    {config.mode}
                  </span>
                </div>
                <div>
                  Auth Required:{" "}
                  <span className="text-muted-foreground font-mono">
                    {isLocalMode ? "No" : "Yes"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className={`h-2 w-2 rounded-full ${isLocalMode ? "bg-yellow-500" : "bg-green-500"}`}
                />
                <span className="text-muted-foreground text-sm">
                  {isLocalMode ? "Local Mode Active" : "Connected to Remote"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

        {/* Configuration Dialogs */}
        <ModeConfigDialog
          open={modeDialogOpen}
          onOpenChange={setModeDialogOpen}
        />

        <LocalConfigDialog
          open={localDialogOpen}
          onOpenChange={setLocalDialogOpen}
        />

        <RemoteConfigDialog
          open={remoteDialogOpen}
          onOpenChange={setRemoteDialogOpen}
        />
      </div>
    </div>
  );
}
